import { createContext, useCallback, useContext } from 'react';

/**
 * Where a list's rows link. Unset, a row opens its store-wide page (`/payments/<id>`); inside a
 * page's screen it is that screen's address, so the row opens under the page and keeps its sidebar.
 */
export const DetailBaseContext = createContext(null);

export function useDetailHref() {
  const base = useContext(DetailBaseContext);
  return useCallback(
    href => {
      if (!base || !href) return href;
      return `${base}/${href.split('/').filter(Boolean).pop()}`;
    },
    [base],
  );
}

/**
 * The page whose screens are showing: `{ pagePath, screens, guestsAreAttendees }`, or null outside a
 * page. With group registration on, the event's attendees screen lists attendees, not customers.
 */
export const PageScopeContext = createContext(null);

const CUSTOMER_SCREENS = ['customers', 'leads', 'attendees'];

/**
 * A link from inside a detail to another record (`screen` is its store-wide list: payments,
 * customers, attendees ...). Inside a page it opens under the page when the record belongs to it and
 * the page has that screen; anything else opens store-wide. A customer always belongs to the page, and
 * an attendee opens under the event's attendees screen while that screen lists attendees.
 */
export function usePageRecordHref() {
  const scope = useContext(PageScopeContext);
  return useCallback(
    (screen, id, recordPage) => {
      const storeWide = `/${screen}/${id}`;
      if (!scope || !id) return storeWide;
      if (screen === 'attendees' && !scope.guestsAreAttendees) return storeWide;
      const target =
        screen === 'customers'
          ? CUSTOMER_SCREENS.find(
              path =>
                scope.screens.includes(path) && !(path === 'attendees' && scope.guestsAreAttendees),
            )
          : scope.screens.includes(screen) && screen;
      if (!target) return storeWide;
      if (screen !== 'customers') {
        const recordPageId = recordPage?._id ?? recordPage;
        if (!recordPageId || `${scope.pagePath}` !== `/pages/${recordPageId}`) return storeWide;
      }
      return `${scope.pagePath}/${target}/${id}`;
    },
    [scope],
  );
}
